import { z } from 'zod';

/**
 * AI LOGIC: Shared schemas and prompt generators for LLM integration.
 * V7.2: Total Data Unification. Merges all micros into a single factual object.
 */

export const DiscoveredMicroSchema = z.object({
    name: z.string().describe("Name of the nutrient (e.g., Selenium, Lycopene, Calcium, Glycine)"),
    amount: z.number().describe("Numerical amount"),
    unit: z.string().describe("Unit (e.g., mg, mcg, IU, g)")
});

export const MicroSchema = z.object({
    fiber: z.object({ amount: z.number(), unit: z.string() }).optional(),
    complex_carbs: z.object({ amount: z.number(), unit: z.string() }).optional(),
    glucose: z.object({ amount: z.number(), unit: z.string() }).optional(),
    fructose: z.object({ amount: z.number(), unit: z.string() }).optional(),
    sucrose: z.object({ amount: z.number(), unit: z.string() }).optional(),
    lactose: z.object({ amount: z.number(), unit: z.string() }).optional(),
    intact_protein: z.object({ amount: z.number(), unit: z.string() }).optional(),
    free_amino_acids: z.object({ amount: z.number(), unit: z.string() }).optional(),
    collagen_peps: z.object({ amount: z.number(), unit: z.string() }).optional(),
    sodium: z.object({ amount: z.number(), unit: z.string() }).optional(),
    potassium: z.object({ amount: z.number(), unit: z.string() }).optional(),
    magnesium: z.object({ amount: z.number(), unit: z.string() }).optional(),
    zinc: z.object({ amount: z.number(), unit: z.string() }).optional(),
    iron: z.object({ amount: z.number(), unit: z.string() }).optional(),
    vitamin_c: z.object({ amount: z.number(), unit: z.string() }).optional(),
    vitamin_d: z.object({ amount: z.number(), unit: z.string() }).optional(),
    vitamin_b12: z.object({ amount: z.number(), unit: z.string() }).optional(),
    omega_3: z.object({ amount: z.number(), unit: z.string() }).optional(),
    leucine: z.object({ amount: z.number(), unit: z.string() }).optional(),
    glycine: z.object({ amount: z.number(), unit: z.string() }).optional(),
    caffeine: z.object({ amount: z.number(), unit: z.string() }).optional()
}).catchall(z.object({ amount: z.number(), unit: z.string() })).describe("Object of all identified micros and clinical fractions");

export const MealItemSchema = z.object({
    item_name: z.string().describe("Clean tactical name of the specific food item"),
    numeric_amount: z.number().describe("Exact numerical amount"),
    unit: z.enum(["g", "ml"]).describe("MUST BE 'g' or 'ml'"),
    calories: z.number().describe("Energy in kcal"),
    protein: z.number().describe("Protein in grams"),
    carbs: z.number().describe("Total Carbohydrates in grams"),
    fat: z.number().describe("Fats in grams"),
    food_insulin_index: z.number().describe("Estimated Food Insulin Index (FII)"),
    insulin_load: z.number().describe("Estimated insulin load score"),
    insulin_spike_risk: z.enum(["Low", "Medium", "High"]),
    sequencing_strategy: z.enum(['MIXED', 'CARBS_FIRST', 'FIBER_PROTEIN_FIRST']).optional().describe("Detected or preferred meal order"),
    // UNIT-AWARE MICROS: AI returns an object of tuples { amount, unit }
    micros: MicroSchema,
    discovered_micros: z.array(DiscoveredMicroSchema).optional().describe("Any additional interesting compounds, vitamins, or minerals discovered in this food.")
});

export const NutritionLedgerSchema = z.object({
    items: z.array(MealItemSchema),
    summary_notes: z.string().describe("Brief tactical rationale")
});

export const MetabolicAdviceSchema = z.object({
    advice: z.string().describe("ONE short, highly tactical piece of metabolic advice")
});

// 2. WORKOUT SCHEMAS
export const BurnEstimationSchema = z.object({
    kcal_burn: z.number().describe("Final estimated active calories burned"),
    confidence_score: z.number().describe("Certainty of estimation (0-1)"),
    logic_summary: z.string().describe("Tactical rationale"),
    modality: z.enum(['STRENGTH', 'CARDIO', 'HYBRID', 'MOBILITY']).describe("Detected training modality")
});

// 3. PROMPT GENERATORS
export const AIPrompts = {
    nutritionParser: (text: string) => `
        CRITICAL: Decompose this meal description into a structured clinical spectrum.
        Text: "${text}"
        
        MANDATORY CLINICAL FRACTIONING (within 'micros'):
        - You MUST decompose Total Carbs into: 'glucose' (fast sugars), 'fructose' (fruit sugars), 'sucrose', 'lactose', and 'complex_carbs' (starches).
        - You MUST decompose Total Protein into: 'intact_protein' (food sources like meat/eggs), 'free_amino_acids' (supplements/free-form), and 'collagen_peps'.
        - SUM CHECK: The sum of these fractions MUST equal the total macro count. 
        - Example: 100g Rice -> carbs: 80, micros: { complex_carbs: { amount: 80, unit: 'g' } }.
        
        METABOLIC & INSULIN RESPONSE:
        - Identify 'sequencing_strategy' if the user mentions order (e.g. "salad first" -> 'FIBER_PROTEIN_FIRST').
        - clinical FII calibration (White Bread=100, Eggs=31, etc).

        DEEP NUTRITIONAL ANALYSIS:
        - Estimate Leucine (mTOR) and Glycine (collagen) within 'micros'.
        - Estimate Fiber, Potassium, Magnesium, Zinc, Iron, and Vitamin C.
        - ALWAYS provide units (mg, mcg, IU, g). Use 'mg' wherever possible (except macros/fractions which use 'g').
    `,

    metabolicCoach: (stats: any, profile: any) => `
        You are an elite Metabolic Coach. Analyze athlete's 4 Biological Pillars.
        USER: ${profile.gender}, Sensitivity: ${profile.insulin_sensitivity}.
        PEAK INSULIN: ${stats.consumed.peak_insulin}.
        TOTAL LOAD: ${stats.consumed.insulin_load}.
        Analyze the provided micro-nutrient trends and provide ONE tactical correction.
    `,

    workoutRefinement: (physicsBaseline: number, weightKg: number, profile: any, duration: number, exerciseSummary: any) => `
        Analyze training session and refine calculated PHYSICS BASELINE: ${physicsBaseline} KCAL.
        PROFILE: ${weightKg}kg | ${profile?.gender || 'MALE'} | Age ${profile?.birth_date ? (new Date().getFullYear() - new Date(profile.birth_date).getFullYear()) : '30'}
        DATA: Duration ${duration}m | Logs: ${JSON.stringify(exerciseSummary)}
        Adjust baseline by +/- 20% for TEMPO, RPE, COMPLEXITY.
    `
};
